import semver from 'semver'
import get from 'lodash/get'
import find from 'lodash/find'
import groupBy from 'lodash/groupBy'
import last from 'lodash/last'
import { capitalize } from 'lodash'
import { FAStyleIdDisplayOrder, FA5StyleIdDisplayOrder } from '@fortawesome-internal-tools/fontawesome-icons/legacy' // eslint-disable-line import/no-unresolved
import {
  FAFamilyIdToFamilyName,
  FAStylesByShorthand,
  KitFamilyIdsThatAreDuotone,
  FAFamilyIdsThatAreDuotone,
  FAStyleStatusByFamilyId,
  KitStylesByShorthand
} from '@fortawesome-internal-tools/fontawesome-icons/canonical' // eslint-disable-line import/no-unresolved

const releases = FONTAWESOME.RELEASES.map((r) => {
  const [year, month, day] = r.date.split('-').map((p) => parseInt(p, 10))
  const date = new Date(year, month - 1, day)

  return {
    ...r,
    date
  }
})

const groupSeed = releases.reduce((acc, release) => {
  let {
    versionParts: { major }
  } = release

  if (!acc[major]) {
    acc[`v${major}`] = []
  }

  return acc
}, {})

function group(releaseList) {
  return {
    ...groupSeed,
    ...groupBy(releaseList, (r) => `v${r.versionParts.major}`),
    together: releaseList
  }
}

function greatestOne(groupings, params = {}) {
  const { fallbacks = {} } = params

  return Object.keys(groupings).reduce((acc, key) => {
    const fallback = get(fallbacks, key)
    acc[key] = groupings[key].slice(-1)[0] || fallback
    return acc
  }, {})
}

export const PRO_CDN_STARTING_VERSION = '5.0.4'
export const KITS_STARTING_VERSION = '5.7.0'
export const DUOTONE_STARTING_VERSION = '5.10.0'
export const THIN_STARTING_VERSION = '6.0.0'
export const V4_FONT_FACE_SHIM_STARTING_VERSION = '5.10.0'
export const CONFLICT_DETECTION_STARTING_VERSION = '5.10.0'
export const NPM_DOWNLOAD_VERSION = '5.4.0'
export const ICON_UPLOAD_STARTING_VERSION = '5.15.1'
export const KIT_DOWNLOAD_STARTING_VERSION = FONTAWESOME.MINIMUM_VERSIONS['kitDownload']
export const CUSTOM_SUBSET_STARTING_VERSION = FONTAWESOME.MINIMUM_VERSIONS['customSubsetting']
export const CUSTOM_DUOTONE_DOWNLOAD_STARTING_VERSION = FONTAWESOME.MINIMUM_VERSIONS['customDuotoneDownload']
export const STYLING_CUSTOM_ROTATION_STARTING_VERSION = '6.0.0'
export const STYLING_ALL_SIZES_STARTING_VERSION = '6.0.0'
export const SUBSET_BY_STYLE_STARTING_VERSION = FONTAWESOME.MINIMUM_VERSIONS['autoSubsettingByStyle']
export const CSS_EMBED_STARTING_VERSION = '6.0.0'
export const DESKTOP_SUBSETTER_LAST_VERSION = '6.4.0'
export const KIT_NPM_DOWNLOAD_STARTING_VERSION = '6.4.0'
export const DUOTONE_FAMILY_STARTING_VERSION = '6.6.0'
export const FULL_SVG_STARTING_VERSION = '7.0.0'
export const ICON_WIZARD_MODIFIER_PLACEMENT_STARTING_VERSION = '7.0.0'
export const all = group(releases)
export const full = group(releases.filter((r) => r.versionParts.pre === null))
export const pre = group(releases.filter((r) => r.versionParts.pre !== null))
export const latest = greatestOne(all, { fallbacks: greatestOne(pre) })
export const preview = greatestOne(pre)

export const allLatest = Object.entries(latest)
  .filter(([key]) => key.startsWith('v'))
  .map(([_, value]) => value)
  .sort((a, b) => {
    const majorDiff = b.versionParts.major - a.versionParts.major
    if (majorDiff !== 0) return majorDiff

    return semver.rcompare(b.version, a.version)
  })

export function getMajorVersion(versionString) {
  return semver.parse(versionString).major
}

export function hasFullSvgSupport(majorVersion) {
  return majorVersion >= getMajorVersion(FULL_SVG_STARTING_VERSION)
}

export function shortPath(version) {
  const versionParts = semver.parse(version)

  return `v${versionParts.major}.${versionParts.minor}`
}

export function matching({ version }) {
  if (!version) {
    return null
  }

  return find(all.together, (release) => {
    return release.version === version
  })
}

const rangePattern = /([0-9]+)\.x/

export function latestForRange({ range }) {
  const rangeMatch = (range || '').match(rangePattern)

  if (!rangeMatch) return

  const versions = get(all, [`v${rangeMatch[1]}`], [])
  return (last(versions) || {}).version
}

function looksLikeRange(maybeRange) {
  return rangePattern.test(maybeRange || '')
}

export function resolve({ version }) {
  if (version === 'latest') {
    // latest should return the latest version FIVE (NOT SIX)
    return latest.v5.version
  }

  if (version === 'preview') {
    return preview.v6.version
  }

  if (looksLikeRange(version)) {
    return latestForRange({ range: version })
  }

  return version
}

export function supportFor(releaseOrVersion) {
  const result = {
    proCdn: false,
    kits: false,
    duotone: false,
    v4FontFaceShim: false,
    conflictDetection: false,
    npmDownload: false,
    iconUpload: false,
    kitDownload: false,
    kitNpmDownload: false,
    customSubset: false,
    cssEmbed: false,
    duotoneFamily: false,
    customDuotoneDownload: false
  }

  let version = null

  if (releaseOrVersion === 'latest') {
    releaseOrVersion = '5.x'
  }

  if (looksLikeRange(releaseOrVersion)) {
    version = latestForRange({ range: releaseOrVersion })
  } else if (typeof releaseOrVersion === 'string') {
    version = releaseOrVersion
  } else if (typeof get(releaseOrVersion, ['version']) === 'string') {
    version = releaseOrVersion.version
  }

  if (!version) return result

  const parsedVersion = semver.parse(version)
  const simpleVersion = `${parsedVersion.major}.${parsedVersion.minor}.${parsedVersion.patch}`

  result.proCdn = semver.gte(simpleVersion, PRO_CDN_STARTING_VERSION)
  result.kits = semver.gte(simpleVersion, KITS_STARTING_VERSION)
  result.duotone = semver.gte(simpleVersion, DUOTONE_STARTING_VERSION)
  result.v4FontFaceShim = semver.gte(simpleVersion, V4_FONT_FACE_SHIM_STARTING_VERSION)
  result.conflictDetection = semver.gte(simpleVersion, CONFLICT_DETECTION_STARTING_VERSION)
  result.npmDownload = semver.gte(simpleVersion, NPM_DOWNLOAD_VERSION)
  result.iconUpload = semver.gte(simpleVersion, ICON_UPLOAD_STARTING_VERSION)
  result.kitDownload = semver.gte(simpleVersion, KIT_DOWNLOAD_STARTING_VERSION)
  result.kitNpmDownload = semver.gte(simpleVersion, KIT_NPM_DOWNLOAD_STARTING_VERSION)
  result.customSubset = semver.gte(simpleVersion, CUSTOM_SUBSET_STARTING_VERSION)
  result.cssEmbed = semver.gte(simpleVersion, CSS_EMBED_STARTING_VERSION)
  result.subsetByStyle = semver.gte(simpleVersion, SUBSET_BY_STYLE_STARTING_VERSION)
  result.duotoneFamily = semver.gte(simpleVersion, DUOTONE_FAMILY_STARTING_VERSION)
  result.customDuotoneDownload = semver.gte(simpleVersion, CUSTOM_DUOTONE_DOWNLOAD_STARTING_VERSION)

  return result
}

export function styleOptions(version, family) {
  if (version === 5) {
    return [
      { label: 'Solid', value: 'solid' },
      { label: 'Regular', value: 'regular' },
      { label: 'Light', value: 'light' },
      { label: 'Duotone', value: 'duotone' }
    ]
  } else {
    return FAStyleStatusByFamilyId.get(family).styleIds.reduce((acc, style) => {
      if (style !== 'brands') {
        acc.push({ value: style, name: capitalize(style), shortCode: getShortCode(family, style) })
      }
      return acc
    }, [])
  }
}

export function classicFreeStyleOptions() {
  return {
    name: 'classic',
    styles: [
      { icon: 'fas fa-icons', label: 'Solid', value: 'solid' },
      { icon: 'far fa-icons', label: 'Regular', value: 'regular' }
    ]
  }
}

export function animationOptions(version) {
  if (version === 5) {
    return [
      { name: 'No Animation', value: null, icon: 'faBan' },
      { name: 'Spin', value: 'spin', icon: 'faRotateRight' },
      { name: 'Pulse', value: 'pulse', icon: 'faRotateLeft' }
    ]
  } else {
    return [
      { name: 'No Animation', value: null, icon: 'faBan' },
      { name: 'Beat', value: 'beat', icon: 'faHeart' },
      { name: 'Beat Fade', value: 'beat-fade', icon: 'faHeartHalfStroke' },
      { name: 'Bounce', value: 'bounce', icon: 'faCircle' },
      { name: 'Fade', value: 'fade', icon: 'faSlidersSimple' },
      { name: 'Flip', value: 'flip', icon: 'faReflectHorizontal' },
      { name: 'Shake', value: 'shake', icon: 'faBellRing' },
      { name: 'Spin', value: 'spin', icon: 'faRotateRight' },
      { name: 'Spin Reverse', value: 'spin-reverse', icon: 'faRotateLeft' },
      { name: 'Spin Pulse', value: 'spin-pulse', icon: 'faSpinner' }
    ]
  }
}

export function familyOptions(version) {
  const releaseVersion = `v${version}`
  const allReleasesInVersion = all[releaseVersion]
  const lengthOfAllReleasesInVersion = allReleasesInVersion.length
  const lastReleaseInVersion = allReleasesInVersion[lengthOfAllReleasesInVersion - 1]
  const versionFamilies = Object.keys(lastReleaseInVersion.familyStyleCounts)

  const familyOptions = versionFamilies.map((family) => {
    return {
      value: family,
      name: FAFamilyIdToFamilyName[family]
    }
  })

  return familyOptions
}

export function rotationOptions() {
  return [
    { name: 'No Rotation', value: null },
    { name: '90', value: 'rotate-90' },
    { name: '180', value: 'rotate-180' },
    { name: '270', value: 'rotate-270' }
  ]
}

export function flippingOptions() {
  return [
    { name: 'No Flip', value: null, id: 'flip-none', family: 'solid', icon: 'faBan' },
    { name: 'Flip Horizontal', value: 'flip-horizontal', id: 'flip-horizontal', family: 'solid', icon: 'faReflectHorizontal' },
    { name: 'Flip Vertical', value: 'flip-vertical', id: 'flip-vertical', family: 'solid', icon: 'faReflectVertical' },
    { name: 'Flip Both', value: 'flip-both', id: 'flip-both', family: 'custom', icon: 'faReflectBoth' }
  ]
}

export function sizeOptions(version) {
  if (version === 5) {
    return [
      { name: 'xs', value: 'xs' },
      { name: 'sm', value: 'sm' },
      { name: 'Default Size', value: null },
      { name: 'lg', value: 'lg' },
      { name: '1x', value: '1x' },
      { name: '2x', value: '2x' },
      { name: '3x', value: '3x' },
      { name: '4x', value: '4x' },
      { name: '5x', value: '5x' },
      { name: '6x', value: '6x' },
      { name: '7x', value: '7x' },
      { name: '8x', value: '8x' },
      { name: '9x', value: '9x' },
      { name: '10x', value: '10x' }
    ]
  } else {
    return [
      { name: '2xs', value: '2xs' },
      { name: 'xs', value: 'xs' },
      { name: 'sm', value: 'sm' },
      { name: 'Default Size', value: null },
      { name: 'lg', value: 'lg' },
      { name: 'xl', value: 'xl' },
      { name: '2xl', value: '2xl' },
      { name: '1x', value: '1x' },
      { name: '2x', value: '2x' },
      { name: '3x', value: '3x' },
      { name: '4x', value: '4x' },
      { name: '5x', value: '5x' },
      { name: '6x', value: '6x' },
      { name: '7x', value: '7x' },
      { name: '8x', value: '8x' },
      { name: '9x', value: '9x' },
      { name: '10x', value: '10x' }
    ]
  }
}

export function getFamilyStylesByVersion(version) {
  const release = matching({ version: version })
  const results = []

  release.familyStyles.forEach((fs) => {
    results.push({
      family: fs.family,
      style: fs.style,
      displayName: getStyleDisplayName(version, fs.family, fs.style),
      order: getStyleOrder(version, fs.family, fs.style)
    })
  })

  return results.sort((a, b) => (a.order > b.order ? 1 : -1))
}

function getStyleDisplayName(version, family, style) {
  if (semver.lt(version, DUOTONE_FAMILY_STARTING_VERSION) && family === 'duotone' && style === 'solid') {
    return 'duotone'
  } else {
    return style
  }
}

const cardIcons = {
  row1: ['fa-house', 'fa-image'],
  row2: ['fa-calendar', 'fa-alarm-clock']
}

const cardIconsBrands = {
  row1: ['fa-facebook', 'fa-font-awesome'],
  row2: ['fa-x-twitter', 'fa-instagram']
}

export function doesSubsettedKitIncludeFamilyStyle(kit, family, style) {
  if (kit.subset !== null && kit.subset.type === 'custom' && kit.subset.icons) {
    return kit.subset.icons.some((i) => i.family === family && i.style === style)
  } else {
    return false // no subset
  }
}

export function getCardIcons(style) {
  return style == 'brands' ? cardIconsBrands : cardIcons
}

export function getSubsettedCardIcons(kit, family, style) {
  const familyStyle = kit.subset.icons.find((s) => s.family === family && s.style === style)

  return {
    row1: padArray(familyStyle.only.slice(0, 2).map((i) => 'fa-' + i)),
    row2: padArray(familyStyle.only.slice(2, 4).map((i) => 'fa-' + i))
  }
}

function padArray(inputArray) {
  return inputArray.concat(Array(2 - inputArray.length).fill('no-icon'))
}

export function getStyleOrder(version, family, style) {
  if (version === 5) {
    return FA5StyleIdDisplayOrder[family][style]
  } else {
    if (family === 'classic' && style === 'brands') {
      return 99 // move brands to the end of the list
    } else {
      return FAStyleIdDisplayOrder[family][style]
    }
  }
}

export function getLongCode(family, style) {
  return getStyleInfo(family, style).longPrefixId
}

export function getShortCode(family, style) {
  return getStyleInfo(family, style).shortPrefixId
}

function getStyleInfo(family, style) {
  switch (family) {
    case 'kit':
      return KitStylesByShorthand[family]
    case 'kit-duotone':
      return KitStylesByShorthand[family]
    case 'classic':
      return FAStylesByShorthand[style]
    case 'duotone':
      return FAStylesByShorthand[style === 'solid' ? 'duotone' : `duotone-${style}`]
    case 'brands':
      return FAStylesByShorthand['brands']
    default:
      return FAStylesByShorthand[`${family}-${style}`]
  }
}

export function getFamilyDefaultStyle(family) {
  return FAStyleStatusByFamilyId.get(family).defaultStyleId
}

export function isDuotoneFamily(family) {
  // NOTE: this doesn't work for v5 since duotone has a family = classic
  return FAFamilyIdsThatAreDuotone.includes(family) || KitFamilyIdsThatAreDuotone.includes(family)
}

export function getAlgoliaObjectId(version, family, style, icon) {
  switch (family) {
    case 'brands':
      return `i:brands:${icon}`
    default:
      if (style === 'brands') {
        return `i:brands:${icon}`
      } else {
        return `i:${family}:${style}:${icon}`
      }
  }
}

export function getFamilyName(familyId) {
  return FAFamilyIdToFamilyName[familyId]
}
